import { useRef, useCallback, useEffect } from 'react';

/**
 * Hook to manage a time interval with start and stop controls.
 *
 * Provides imperative control over when to start and stop an interval,
 * useful for polling or periodic operations that need to be controlled
 * based on external conditions.
 *
 * @param callback - Function to execute on each interval
 * @param timeout - Interval duration in milliseconds
 * @returns Object with start and stop methods
 *
 * @example
 * const { start, stop } = useTimeInterval(() => {
 *   refetch();
 * }, 2000);
 *
 * // Start polling
 * start();
 *
 * // Stop polling
 * stop();
 */
export function useTimeInterval(
  callback: () => void,
  timeout: number,
): {
  start: () => void;
  stop: () => void;
} {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(callback);

  // Keep callback ref up to date
  // eslint-disable-next-line no-restricted-syntax -- reason: Ref update doesn't need to trigger re-renders
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const start = useCallback(() => {
    // Stop any existing interval before starting a new one
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    intervalRef.current = setInterval(() => {
      callbackRef.current();
    }, timeout);
  }, [timeout]);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  // eslint-disable-next-line no-restricted-syntax -- reason: Cleanup interval on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return { start, stop };
}
